import { useEffect, useRef, useState } from 'react';

const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: '免費諮詢',
      description: '透過線上表單或電話，告訴我們您的需求',
      image: 'https://public.readdy.ai/ai/img_res/1d9d4d15f162d9ed8cc17d67caf5bc2e.jpg',
    },
    {
      number: '02',
      title: '專業鑑定',
      description: '由資深鑑定師進行全面檢測，確保真偽',
      image: 'https://readdy.ai/api/search-image?query=Top%20down%20overhead%20view%20closeup%20shot%20of%20LED%20magnifying%20glass%20examining%20luxury%20handbag%20leather%20surface%2C%20circular%20LED%20ring%20light%20shining%20directly%20downward%20onto%20bag%20texture%2C%20professional%20authentication%20inspection%2C%20magnifier%20glass%20positioned%20above%20the%20handbag%2C%20light%20illuminating%20leather%20details%20below%2C%20clean%20workspace%20background%2C%20professional%20equipment%2C%20black%20and%20white%20photography%20style%2C%20focus%20on%20downward%20lighting%20direction&width=800&height=600&seq=magnifier-topdown-closeup-007&orientation=landscape',
    },
    {
      number: '03',
      title: '出具證書',
      description: '提供詳細的鑑定報告與真品證書',
      image: 'https://static.readdy.ai/image/3b1a2dd7b171b6e7ce06ff7b01b788f6/b39d9b89fef6d35cb90d2d4da44b210a.webp',
    },
    {
      number: '04',
      title: '安心交易',
      description: '完成鑑定後，您可以放心進行交易',
      image: 'https://readdy.ai/api/search-image?query=Happy%20satisfied%20customer%20receiving%20authenticated%20luxury%20handbag%20in%20elegant%20gift%20box%2C%20smiling%20with%20confidence%2C%20modern%20luxury%20boutique%20interior%2C%20clean%20white%20background%2C%20professional%20service%20completion%20moment%2C%20premium%20shopping%20experience%2C%20natural%20lighting%2C%20joyful%20atmosphere%2C%20black%20and%20white%20photography%20style&width=800&height=600&seq=process04&orientation=landscape',
    },
  ];

  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="process" className="relative py-28 lg:py-36 bg-black overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute left-[25%] top-0 bottom-0 w-px bg-white"></div>
        <div className="absolute left-[50%] top-0 bottom-0 w-px bg-white"></div>
        <div className="absolute left-[75%] top-0 bottom-0 w-px bg-white"></div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-px bg-white/30"></div>
            <span className="text-[11px] font-body font-medium tracking-[0.25em] text-white/50 uppercase">
              Authentication Process
            </span>
            <div className="w-10 h-px bg-white/30"></div>
          </div>
          <h2 className="font-heading text-5xl lg:text-7xl text-white leading-[0.95] tracking-wide mb-6">
            鑑定流程
          </h2>
          <p className="font-body text-[14px] text-white/60 leading-relaxed max-w-2xl mx-auto">
            四步驟完成專業鑑定，為您的資產提供科學憑證
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative group transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {/* Card */}
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                {/* Number Badge */}
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-white flex items-center justify-center border-4 border-black">
                  <span className="font-heading text-2xl text-black">{step.number}</span>
                </div>

                {/* Image or Icon */}
                {step.image ? (
                  <div className="w-full h-48 mb-6 mt-4 overflow-hidden">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-full object-cover object-center grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 flex items-center justify-center mb-6 mt-4">
                    <i className={`${step.icon} text-4xl text-white/70 group-hover:text-white transition-colors`}></i>
                  </div>
                )}

                {/* Content */}
                <h3 className="font-heading text-2xl text-white mb-4 tracking-wide">
                  {step.title}
                </h3>
                <p className="font-body text-[13px] text-white/60 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Connector Line (Desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-white/20 z-10"></div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <a
            href="#appointment"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-black font-body text-[14px] font-semibold tracking-wider hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            立即預約鑑定
            <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
